import * as d3 from "d3";
import _ from "lodash";
import moment from "moment";
import { getSiteID } from "./data-management";
import { mean } from "mathjs";

function shadeColor(color) {
  let R = Math.round((parseInt(color.substring(1, 3), 16) + 255) / 2);
  let G = Math.round((parseInt(color.substring(3, 5), 16) + 255) / 2);
  let B = Math.round((parseInt(color.substring(5, 7), 16) + 255) / 2);

  R = R < 255 ? R : 255;
  G = G < 255 ? G : 255;
  B = B < 255 ? B : 255;

  const RR = R.toString(16).length == 1 ? "0" + R.toString(16) : R.toString(16);
  const GG = G.toString(16).length == 1 ? "0" + G.toString(16) : G.toString(16);
  const BB = B.toString(16).length == 1 ? "0" + B.toString(16) : B.toString(16);
  return "#" + RR + GG + BB;
}

function padZero(str, lenOld) {
  let len = lenOld;
  len = len || 2;
  const zeros = new Array(len).join("0");
  return (zeros + str).slice(-len);
}

function invertColor(hexOld) {
  let hex = hexOld;
  if (hex.indexOf("#") === 0) {
    hex = hex.slice(1);
  }
  // convert 3-digit hex to 6-digits.
  if (hex.length === 3) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }
  if (hex.length !== 6) {
    throw new Error("Invalid HEX color.");
  }
  // invert color components
  const r = (255 - parseInt(hex.slice(0, 2), 16)).toString(16);

  const g = (255 - parseInt(hex.slice(2, 4), 16)).toString(16);

  const b = (255 - parseInt(hex.slice(4, 6), 16)).toString(16);
  // pad each with zeros and return
  const inverColor = "#" + padZero(r) + padZero(g) + padZero(b);
  return inverColor === "#ffffff" ? "#dcdcdc" : inverColor;
}

const getYValueFromGroup = group => {
  let groupValues = null;
  if (group.includes("compliance")) {
    groupValues = group.split("compliance-").pop();
  } else if (group.includes("completion")) {
    groupValues = group.split("completion-").pop();
  }
  switch (groupValues) {
    case "0":
      return 0;
    case "0-10":
      return 5;
    case "1-10":
      return 5;
    case "10":
      return 10;
    case "10-20":
      return 15;
    case "11-20":
      return 15;
    case "20":
      return 20;
    case "20-30":
      return 25;
    case "21-30":
      return 25;
    case "30":
      return 30;
    case "30-40":
      return 35;
    case "31-40":
      return 35;
    case "40":
      return 40;
    case "40-50":
      return 45;
    case "41-50":
      return 45;
    case "50":
      return 50;
    case "50-60":
      return 55;
    case "51-60":
      return 55;
    case "60":
      return 60;
    case "60-70":
      return 65;
    case "61-70":
      return 65;
    case "70":
      return 70;
    case "70-80":
      return 75;
    case "71-80":
      return 75;
    case "80":
      return 80;
    case "80-90":
      return 85;
    case "81-90":
      return 85;
    case "90":
      return 90;
    case "90-99":
      return 95;
    case "91-99":
      return 95;
    case "100":
      return 100;
  }
};

const round5 = x => {
  const remainder = x % 10;
  if (remainder > 0 && remainder < 5) {
    return Math.ceil(x / 5) * 5;
  }
  return Math.floor(x / 5) * 5;
};

const processBubbleData = (complianceRawData, countrySelected, sitesSelected, filterDateMin, filterDateMax, show = "compliance") => {
  const bubbleData = [
    {
      name: show === "compliance" ? "Compliance Rate" : "Completion Rate",
      data: []
    }
  ];

  const bubbleDataByCountry = _.cloneDeep(bubbleData);
  const bubbleDataBySite = _.cloneDeep(bubbleData);
  let maxDataValue = 0; // All these min max value to define the bubble radius using d3.scaler
  let minDataValue = Number.MAX_SAFE_INTEGER;
  let maxSiteValue = 0;
  let minSiteValue = Number.MAX_SAFE_INTEGER;
  let maxCountryValue = 0;
  let minCountryValue = Number.MAX_SAFE_INTEGER;
  for (const dateData of complianceRawData) {
    const dateTime = new Date(moment(dateData.key).add(1, "days")).getTime();
    if (filterDateMin && filterDateMin >= dateTime) {
      continue;
    }
    if (filterDateMax && filterDateMax <= dateTime) {
      continue;
    }
    const dataMap = new Map();
    const dataMapByCountry = new Map();
    const dataMapBySite = new Map();
    for (const countryData of dateData.values) {
      if (countrySelected !== "all" && countrySelected[0] !== "all" && !countrySelected.includes(countryData.key)) {
        continue;
      }
      let countryTotalValue = 0;
      let countryCounter = 0;
      let countryList = null;
      for (const siteData of countryData.values) {
        if (sitesSelected[0] !== "all" && !sitesSelected.includes(siteData.key)) {
          continue;
        }
        let siteTotalValue = 0;
        let siteCounter = 0;
        let siteList = null;
        const columnSelector = show === "compliance" ? "complianceColumnList" : "completionColumnList";
        const valueSelector = show === "compliance" ? "complianceValueList" : "completionValueList";
        const dataGroups = siteData.values[0][columnSelector].split(";");
        const dataValues = siteData.values[0][valueSelector].split(";");
        if (dataGroups.length <= 0 || dataGroups[0] === "") {
          continue;
        }
        for (let index = 0; index < dataGroups.length; index++) {
          const activitiesDatas = dataValues[index].split("||");
          for (let i = 0; i < activitiesDatas.length; i++) {
            if (activitiesDatas[i].includes("**")) {
              const element = activitiesDatas[i].split("**");
              const activityName = element[0];
              const activityNumberInformation = element[1].split("::");
              const activityNumber = Number(activityNumberInformation[0]);
              const activityInfo = activityNumberInformation[1].split("++");
              const activityDataPercentage = Number(activityInfo[0]);
              const activityDataDate = activityInfo[1];
              const activityDataValue = activityDataPercentage * activityNumber;
              countryTotalValue += activityDataValue;
              countryCounter += activityNumber;
              siteTotalValue += activityDataValue;
              siteCounter += activityNumber;
              if (dataMap.has(activityName)) {
              const { activityTotalValue, activityCounter, activityDate } = dataMap.get(activityName);
                dataMap.set(activityName, {
                  activityTotalValue: activityTotalValue + activityDataValue,
                  activityCounter: activityCounter + activityNumber,
                  activityDate: moment(activityDataDate).isAfter(moment(activityDate, "DD MMM YYYY")) ? moment(activityDataDate).format("DD MMM YYYY") : activityDate
                });
              } else {
                dataMap.set(activityName, {
                  activityTotalValue: activityDataValue,
                  activityCounter: activityNumber,
                  activityDate: moment(activityDataDate).format("DD MMM YYYY")
                });
              }
            }
          }
        }

        // every loop for a site, calculate that site compliance/completion average rate
        if (siteCounter === 0) {
          continue;
        }
        const siteRealAverage = Math.round(siteTotalValue / siteCounter);
        const siteRoundedToFiveAverage = round5(siteRealAverage);
        if (dataMapBySite.has(siteRoundedToFiveAverage)) {
          siteList = dataMapBySite.get(siteRoundedToFiveAverage);
        } else {
          siteList = [];
        }
        const siteInfo = {
          siteName: siteData.key,
          siteRate: siteRealAverage
        };
        siteList.push(siteInfo);
        dataMapBySite.set(siteRoundedToFiveAverage, siteList);
      }
      // every loop for a country, calculate that country compliance/completion average rate
      if (countryCounter === 0) {
        continue;
      }
      const countryRealAverage = Math.round(countryTotalValue / countryCounter);
      const countryRoundedToFiveAverage = round5(countryRealAverage);
      if (dataMapByCountry.has(countryRoundedToFiveAverage)) {
        countryList = dataMapByCountry.get(countryRoundedToFiveAverage);
      } else {
        countryList = [];
      }
      const countryInfo = {
        countryName: countryData.key,
        countryRate: countryRealAverage
      };
      countryList.push(countryInfo);
      dataMapByCountry.set(countryRoundedToFiveAverage, countryList);
    }
    const activityMap = new Map();
    for (const [activityName, activityData] of dataMap) {
      const activityRealAverage = Math.round(activityData.activityTotalValue / activityData.activityCounter);
      const activityRoundedToFiveAverage = round5(activityRealAverage);
      let activityList = [];
      if (activityMap.has(activityRoundedToFiveAverage)) {
        activityList = activityMap.get(activityRoundedToFiveAverage);
      }
      const activityInfo = {
        activityName,
        activityRealAverage,
        activityDate: activityData.activityDate
      };
      activityList.push(activityInfo);
      activityMap.set(activityRoundedToFiveAverage, activityList);
    }
    for (const [key, value] of activityMap) {
      const numberOfActivities = value.length;
      if (numberOfActivities >= maxDataValue) {
        maxDataValue = numberOfActivities;
      }
      if (numberOfActivities <= minDataValue) {
        minDataValue = numberOfActivities;
      }
      bubbleData[0].data.push([dateTime, Number(key) || 0, Number(numberOfActivities) || 0, Number(numberOfActivities) || 0, value]); // format is (dateTime, bubbleRadius, realValue, ....(for other purpose))
    }
    for (const [key, value] of dataMapBySite) {
      const numberOfSites = value.length;
      if (numberOfSites >= maxSiteValue) {
        maxSiteValue = numberOfSites;
      }
      if (numberOfSites <= minSiteValue) {
        minSiteValue = numberOfSites;
      }
      bubbleDataBySite[0].data.push([dateTime, Number(key) || 0, Number(numberOfSites) || 0, Number(numberOfSites) || 0, value]);
    }
    for (const [key, value] of dataMapByCountry) {
      const numberOfCountries = value.length;
      if (numberOfCountries >= maxCountryValue) {
        maxCountryValue = numberOfCountries;
      }
      if (numberOfCountries <= maxCountryValue) {
        minCountryValue = numberOfCountries;
      }
      bubbleDataByCountry[0].data.push([dateTime, Number(key) || 0, Number(numberOfCountries) || 0, Number(numberOfCountries) || 0, value]);
    }
  }

  // process scaling Z val. Below lines will set the radius of bubble using log scale
  const bubbleRadius = d3
    .scaleSymlog()
    .domain([minDataValue, maxDataValue])
    .range([20, 50]);
  const siteBubbleRadius = d3
    .scaleSymlog()
    .domain([minSiteValue, maxSiteValue])
    .range([20, 50]);
  const countryBubbleRadius = d3
    .scaleSymlog()
    .domain([minCountryValue, maxCountryValue])
    .range([20, 50]);
  for (let index = 0; index < bubbleData[0].data.length; index++) {
    const seriesElement = bubbleData[0].data[index];
    if (seriesElement[2] === 0) {
      continue;
    }
    seriesElement[2] = Math.abs(Math.round(bubbleRadius(seriesElement[2])));
  }
  for (let index = 0; index < bubbleDataBySite[0].data.length; index++) {
    const seriesElement = bubbleDataBySite[0].data[index];
    if (seriesElement[2] === 0) {
      continue;
    }
    seriesElement[2] = Math.abs(Math.round(siteBubbleRadius(seriesElement[2])));
  }
  for (let index = 0; index < bubbleDataByCountry[0].data.length; index++) {
    const seriesElement = bubbleDataByCountry[0].data[index];
    if (seriesElement[2] === 0) {
      continue;
    }
    seriesElement[2] = Math.abs(Math.round(countryBubbleRadius(seriesElement[2])));
  }

  return {
    bubbleData,
    bubbleDataByCountry,
    bubbleDataBySite
  };
};
const processComplianceDataByParticipant = (complianceRawData, countrySelected, sitesSelected, filterDateMin, filterDateMax, show = "compliance") => {
  const bubbleData = [
    {
      name: show === "compliance" ? "Compliance Rate" : "Completion Rate",
      data: []
    }
  ];
  let maxDataValue = 0;
  let minDataValue = Number.MAX_SAFE_INTEGER;
  for (const dateData of complianceRawData) {
    const dateTime = new Date(moment(dateData.key).add(1, "days")).getTime();
    if (filterDateMin && filterDateMin >= dateTime) {
      continue;
    }
    if (filterDateMax && filterDateMax <= dateTime) {
      continue;
    }
    const dataMap = new Map();
    for (const countryData of dateData.values) {
      if (countrySelected !== "all" && countrySelected[0] !== "all" && !countrySelected.includes(countryData.key)) {
        continue;
      }
      for (const siteData of countryData.values) {
        if (sitesSelected[0] !== "all" && !sitesSelected.includes(siteData.key)) {
          continue;
        }
        const columnSelector = show === "compliance" ? "complianceColumnList" : "completionColumnList";
        const valueSelector = show === "compliance" ? "complianceValueList" : "completionValueList";
        const dataGroups = siteData.values[0][columnSelector].split(";");
        const dataValues = siteData.values[0][valueSelector].split(";").map(iNum => parseInt(iNum, 10) || 0);
        if (dataGroups.length <= 0 || dataGroups[0] === "") {
          continue;
        }
        for (let index = 0; index < dataGroups.length; index++) {
          const g = dataGroups[index];
          const value = getYValueFromGroup(g);
          const numOfData = dataValues[index];
          if (numOfData > maxDataValue) {
            maxDataValue = numOfData;
          }
          if (numOfData < minDataValue) {
            minDataValue = numOfData;
          }
          if (dataMap.has(value)) {
            dataMap.set(value, Number(dataMap.get(value)) + Number(numOfData));
          } else {
            dataMap.set(value, Number(numOfData));
          }
        }
      }
    }
    for (const [key, value] of dataMap) {
      bubbleData[0].data.push([dateTime, Number(key) || 0, Number(value) || 0, Number(value) || 0]);
    }
  }
  const bubbleRadius = d3
    .scaleSymlog()
    .domain([minDataValue, maxDataValue])
    .range([10, 30]);
  for (let index = 0; index < bubbleData[0].data.length; index++) {
    const seriesElement = bubbleData[0].data[index];
    if (seriesElement[2] === 0) {
      continue;
    }
    seriesElement[2] = Math.abs(Math.round(bubbleRadius(seriesElement[2])));
  }
  const boundaryValue = {
    minDataValue,
    maxDataValue
  };

  return {
    boundaryValue,
    bubbleData
  };
};

function setRatePrefix(type) {
  switch (type) {
    case "completion":
      return "percentageCompletionRate";
    case "compliance":
      return "percentageComplianceRate";
    default:
      return "";
  }
}

function processDataForTable(dataSources = [], rangeRate, dateString, siteListInParticipants, participantsSelected = [], choosenTab = "Activities", type = "completion") {
  if (_.isEmpty(dataSources)) {
    return [];
  }

  let dataIndexForActivities = [];
  const PREFIX_RATE_COMPLIANCE = setRatePrefix(type);
  const PREFIX_DATE = "date";
  const firstDateInMonth = moment(dateString)
    .startOf("month")
    .unix();
  const lastDateInMonth = moment(dateString)
    .endOf("month")
    .unix();

  dataIndexForActivities = _.filter(dataSources, data => {
    if (choosenTab === "Participants" && participantsSelected.length > 0 && participantsSelected[0] !== "all" && !participantsSelected.includes(data.participantId)) {
      return false;
    }
    return data[PREFIX_RATE_COMPLIANCE] >= rangeRate[0] && data[PREFIX_RATE_COMPLIANCE] <= rangeRate[1] && moment(data[PREFIX_DATE]).unix() >= firstDateInMonth && moment(data[PREFIX_DATE]).unix() <= lastDateInMonth;
  });

  dataIndexForActivities =
    dataIndexForActivities.length > 0
      ? dataIndexForActivities.map(d => {
          d.date = moment(d.date)
            .format("DD MMM YYYY")
            .toUpperCase();
          if (choosenTab === "Participants") {
            const participant = siteListInParticipants.find(p => p.participantId === d.participantId);
            d.threadId = d.participantId;
            d.participantId = participant.participantName ? participant.participantName : "";
          } else if (choosenTab === "Sites") {
            const siteId = getSiteID(d.siteName, "name", "userSiteId");
            d.siteName = `${d.siteName} (${siteId})`;
          }
          return d;
        })
      : dataIndexForActivities;

  return new Promise(resolve => {
    resolve(dataIndexForActivities);
  });
}

function getFilterValueFromRate(val) {
  if (val % 10 !== 0) {
    const min = val - 4;
    const max = val + 4; //if range 10-20, means val = 15. get the range between 11-19
    return [min, max];
  }
  return [val, val];
}

const InitRateDataCsv = {
  A: "0-10%",
  B: ">10-20%",
  C: ">20-30%",
  D: ">30-40%",
  E: ">40-50%",
  F: ">50-60%",
  G: ">60-70%",
  H: ">70-80%",
  I: ">80-90%",
  J: ">90-100%"
};

function generateCsvRate(rate) {
  switch (true) {
    // case rate === 0:
    //   return '0';
    case rate >= 0 && rate <= 10:
      return InitRateDataCsv.A;
    // case rate === 10:
    //   return '10';
    case rate > 10 && rate <= 20:
      return InitRateDataCsv.B;
    // case rate === 20:
    //   return '20';
    case rate > 20 && rate <= 30:
      return InitRateDataCsv.C;
    // case rate === 30:
    //   return '30';
    case rate > 30 && rate <= 40:
      return InitRateDataCsv.D;
    // case rate === 40:
    //   return '40';
    case rate > 40 && rate <= 50:
      return InitRateDataCsv.E;
    // case rate === 50:
    //   return '50';
    case rate > 50 && rate <= 60:
      return InitRateDataCsv.F;
    // case rate === 60:
    //   return '60';
    case rate > 60 && rate <= 70:
      return InitRateDataCsv.G;
    // case rate === 70:
    //   return '70';
    case rate > 70 && rate <= 80:
      return InitRateDataCsv.H;
    // case rate === 80:
    //   return '80';
    case rate > 80 && rate <= 90:
      return InitRateDataCsv.I;
    // case rate === 90:
    //   return '90';
    case rate > 90 && rate <= 100:
      return InitRateDataCsv.J;
    // case rate === 100:
    //   return '100';
    default:
      return;
  }
}

function getIntRateValueFromRate(val) {
  if (val % 10 !== 0) {
    const min = val - 5;
    const max = val + 5;
    return `${min}% - ${max}%`;
  }
  return val + "%";
}

function primaryColorFnc() {
  let primaryColor = document.querySelector(":root").style.getPropertyValue("--study-primary-color");
  primaryColor = primaryColor || "#00C3B3";
  return primaryColor;
}

function getContrast(hexcolorOld) {
  let hexcolor = hexcolorOld;
  if (hexcolor.slice(0, 1) === "#") {
    hexcolor = hexcolor.slice(1);
  }

  if (hexcolor.length === 3) {
    hexcolor = hexcolor
      .split("")
      .map(function(hex) {
        return hex + hex;
      })
      .join("");
  }

  const r = parseInt(hexcolor.substr(0, 2), 16) * 299;
  const g = parseInt(hexcolor.substr(2, 2), 16) * 587;
  const b = parseInt(hexcolor.substr(4, 2), 16) * 114;

  const yiq = (r + g + b) / 1000;
  return yiq >= 128 ? "#000000" : "#ffffff";
}

function generateTooltip(data, label) {
  let tooltip = "";
  if (label == "Site Team") {
    tooltip += `<div class='cancelled-telehealth-visits-tooltip site-team' style='color:${getContrast(primaryColorFnc())}'>`;
  } else if (label == "Home Health") {
    tooltip += `<div class='cancelled-telehealth-visits-tooltip' style='background-color: ${shadeColor(primaryColorFnc(), 60)};color:${getContrast(shadeColor(primaryColorFnc(), 60))}'>`;
  } else if (label == "Participant") {
    tooltip += `<div class='cancelled-telehealth-visits-tooltip' style='background-color: ${invertColor(primaryColorFnc())};color:${getContrast(invertColor(primaryColorFnc()))}'>`;
  }

  tooltip += `<div>% (Cancelled / Total Visits)</div>
  <small> <div> $label </div>
    <div> All: ${data.data.allCancelledPercentage}% (${data.data.allCancelled} / ${data.data.allVisits}) </div>
    <div> ${label}: ${data.value}% (${data.data.cancelled} / ${data.data.allVisits}) </div> </small>
  </div>`;

  return tooltip;
}

function getStandardDeviation(array, mean) {
  const n = array.length;
  return parseFloat(Math.sqrt(array.map(x => Math.pow(x - mean, 2)).reduce((a, b) => a + b) / n)).toFixed(2);
}

function generateCsvContent(obj) {
  const data = [];
  const keyData = Object.keys(obj);
  if (keyData.indexOf("label") > -1) {
    data.splice(0, 0, obj["label"]);
  } else {
    data.splice(0, 0, "");
  }
  if (keyData.indexOf(InitRateDataCsv.A) > -1) {
    data.splice(1, 0, obj[InitRateDataCsv.A]);
  } else {
    data.splice(1, 0, Number(0));
  }
  if (keyData.indexOf(InitRateDataCsv.B) > -1) {
    data.splice(2, 0, obj[InitRateDataCsv.B]);
  } else {
    data.splice(2, 0, Number(0));
  }
  if (keyData.indexOf(InitRateDataCsv.C) > -1) {
    data.splice(3, 0, obj[InitRateDataCsv.C]);
  } else {
    data.splice(3, 0, Number(0));
  }
  if (keyData.indexOf(InitRateDataCsv.D) > -1) {
    data.splice(4, 0, obj[InitRateDataCsv.D]);
  } else {
    data.splice(4, 0, Number(0));
  }
  if (keyData.indexOf(InitRateDataCsv.E) > -1) {
    data.splice(5, 0, obj[InitRateDataCsv.E]);
  } else {
    data.splice(5, 0, Number(0));
  }
  if (keyData.indexOf(InitRateDataCsv.F) > -1) {
    data.splice(6, 0, obj[InitRateDataCsv.F]);
  } else {
    data.splice(6, 0, Number(0));
  }
  if (keyData.indexOf(InitRateDataCsv.G) > -1) {
    data.splice(7, 0, obj[InitRateDataCsv.G]);
  } else {
    data.splice(7, 0, Number(0));
  }
  if (keyData.indexOf(InitRateDataCsv.H) > -1) {
    data.splice(8, 0, obj[InitRateDataCsv.H]);
  } else {
    data.splice(8, 0, Number(0));
  }
  if (keyData.indexOf(InitRateDataCsv.I) > -1) {
    data.splice(9, 0, obj[InitRateDataCsv.I]);
  } else {
    data.splice(9, 0, Number(0));
  }
  if (keyData.indexOf(InitRateDataCsv.J) > -1) {
    data.splice(10, 0, obj[InitRateDataCsv.J]);
  } else {
    data.splice(10, 0, Number(0));
  }

  return data;
}

function filterVizDataByDate(dataSources, fromDate, toDate) {
  let tmpData = dataSources && dataSources[0].data ? dataSources[0].data : [];
  const isNotEmptyData = Array.isArray(tmpData) && tmpData.length > 0;
  if (!isNotEmptyData) {
    return [];
  }

  if (fromDate && toDate) {
    tmpData = tmpData.filter(d => {
      return moment(d[0]).isBetween(fromDate, toDate, undefined, "[]");
    });
    dataSources[0].data = tmpData;
  }
  return dataSources;
}

function getComplianceScore(rawData, choosenCountries = [], choosenSites = []) {
  if (!rawData || choosenCountries.length === 0 || choosenSites.length === 0) {
    return;
  }

  let current = 0;
  let last = 0;
  let sites = [];
  Object.keys(rawData).map(country => {
    Object.keys(rawData[country]).map(site => {
      if ((choosenSites.includes("all") || choosenSites.includes(site)) && (choosenCountries.includes("all") || choosenCountries.includes(country))) {
        current += parseFloat(rawData[country][site]["currentCompliance"]);
        last += parseFloat(rawData[country][site]["lastWeekCompliance"]);
        sites.push(site);
      }
    });
  });

  sites = _.uniq(sites);
  current = current / sites.length;
  last = last / sites.length;

  if (current > 0) {
    current = current / 10;
  }
  if (last > 0) {
    last = last / 10;
  }
  const complianceScore = {
    current,
    last
  };
  return complianceScore;
}

function getComplianceInsightNonCompliantActivitiesData(rawData, choosenCountries = [], choosenSites = []) {
  if (!rawData || choosenCountries.length === 0 || choosenSites.length === 0) {
    return;
  }
  const rawDataTest = rawData;
  const series = {};
  const initData = {};
  series["dataset"] = [];
  series["categories"] = [];

  series["dataset"][0] = { data: initData };
  series["categories"][0] = { category: [] };

  const dataTemp = {};
  const nCount = {};
  const tmpSortData = [];

  Object.entries(rawDataTest).forEach(([country, sites]) => {
    Object.entries(sites).forEach(([site, activities]) => {
      Object.entries(activities).forEach(([activity, totalActivity]) => {
        if ((choosenSites.includes("all") || choosenSites.includes(site)) && (choosenCountries.includes("all") || choosenCountries.includes(country))) {
          if (!dataTemp[activity]) {
            dataTemp[activity] = Number(totalActivity);
            nCount[activity] = 1;
          } else {
            dataTemp[activity] += Number(totalActivity);
            nCount[activity] += 1;
          }
        }
      });
    });
  });

  Object.entries(dataTemp).forEach(([activityName, totalActivity]) => {
    Object.entries(nCount).forEach(([activityNameFromCount, totalActivityCount]) => {
      if (activityName === activityNameFromCount) {
        dataTemp[activityName] = Number(totalActivity / totalActivityCount);
      }
    });
  });

  Object.entries(dataTemp).forEach(([activityName, total]) => {
    tmpSortData.push({
      label: activityName,
      total: isNaN(total) ? 0 : total
    });
  });

  // set data into series for tab activities
  const sortedData = _.orderBy(tmpSortData, ["total", tmpSortData => tmpSortData.label.toLowerCase()], ["desc", "asc"]);

  const activityLabel = sortedData.map(item => ({ label: item.label }));
  const totalData = sortedData.map(item => (item.total != 0 ? { value: item.total } : { showValue: 0 }));

  series.categories[0].category = activityLabel;
  series.dataset[0].data = totalData;

  return series;
}

function getCancelledTelehealthVisits(rawData, choosenCountries = [], choosenSites = [], tabIndex = 0) {
  if (_.isEmpty(rawData)) {
    return;
  }
  const series = {};
  const csvData = {};

  const dataSiteTeam = [];
  const dataHomeHealth = [];
  const dataParticipant = [];
  const labels = [];
  const tmpSortedData = [];

  const tabIndexTotal = {};
  const tabIndexCountries = {};
  const tabIndexSite = {};

  let totalCancelledSiteTeam = 0;
  let totalCancelledHomeHealth = 0;
  let totalCancelledParticipant = 0;

  let totalVisitsSiteTeam = 0;
  let totalVisitsHomeHealth = 0;
  let totalVisitsParticipant = 0;

  const totalCountrySiteTeam = {};
  const totalCountryHomeHealth = {};
  const totalCountryParticipant = {};

  const initData = {
    siteTeam: {
      cancelled: 0,
      visits: 0
    },
    homeHealth: {
      cancelled: 0,
      visits: 0
    },
    participant: {
      cancelled: 0,
      visits: 0
    }
  };

  series["dataset"] = [];
  series["categories"] = [];
  csvData["dataset"] = [];
  csvData["categories"] = [];

  let primaryColor = document.querySelector(":root").style.getPropertyValue("--study-primary-color");
  primaryColor = primaryColor || "#7f69d4";
  series["dataset"][0] = {
    seriesname: "Site Team",
    color: primaryColor,
    data: []
  };
  series["dataset"][1] = {
    seriesname: "Home Health",
    color: shadeColor(primaryColor, 60),
    data: []
  };
  series["dataset"][2] = {
    seriesname: "Participant",
    color: invertColor(primaryColor),
    data: []
  };
  series["categories"][0] = { category: [] };

  const PREFIX_SITE_TEAM = "Site_Team";
  const PREFIX_HOME_HEALTH = "HomeHealth";
  const PREFIX_PARTICIPANT = "Participant";

  for (const countryName in rawData.countries) {
    const sites = rawData.countries[countryName];
    for (const siteName in sites) {
      const siteData = sites[siteName];
      if ((choosenSites.includes("all") || choosenSites.includes(siteName)) && (choosenCountries.includes("all") || choosenCountries.includes(countryName))) {
        if (!totalCountrySiteTeam[countryName]) {
          totalCountrySiteTeam[countryName] = { cancelled: 0, total: 0 };
        }
        if (!totalCountryHomeHealth[countryName]) {
          totalCountryHomeHealth[countryName] = { cancelled: 0, total: 0 };
        }
        if (!totalCountryParticipant[countryName]) {
          totalCountryParticipant[countryName] = { cancelled: 0, total: 0 };
        }
        totalCountrySiteTeam[countryName].cancelled += siteData[PREFIX_SITE_TEAM] ? Number(siteData[PREFIX_SITE_TEAM].cancelled_count) : 0;
        totalCountryHomeHealth[countryName].cancelled += siteData[PREFIX_HOME_HEALTH] ? Number(siteData[PREFIX_HOME_HEALTH].cancelled_count) : 0;
        totalCountryParticipant[countryName].cancelled += siteData[PREFIX_PARTICIPANT] ? Number(siteData[PREFIX_PARTICIPANT].cancelled_count) : 0;

        totalCountrySiteTeam[countryName].total += siteData[PREFIX_SITE_TEAM] ? Number(siteData[PREFIX_SITE_TEAM].total_visit) : 0;
        totalCountryHomeHealth[countryName].total += siteData[PREFIX_HOME_HEALTH] ? Number(siteData[PREFIX_HOME_HEALTH].total_visit) : 0;
        totalCountryParticipant[countryName].total += siteData[PREFIX_PARTICIPANT] ? Number(siteData[PREFIX_PARTICIPANT].total_visit) : 0;

        totalCancelledSiteTeam += siteData[PREFIX_SITE_TEAM] ? Number(siteData[PREFIX_SITE_TEAM].cancelled_count) : 0;
        totalCancelledHomeHealth += siteData[PREFIX_HOME_HEALTH] ? Number(siteData[PREFIX_HOME_HEALTH].cancelled_count) : 0;
        totalCancelledParticipant += siteData[PREFIX_PARTICIPANT] ? Number(siteData[PREFIX_PARTICIPANT].cancelled_count) : 0;

        totalVisitsSiteTeam += siteData[PREFIX_SITE_TEAM] ? Number(siteData[PREFIX_SITE_TEAM].total_visit) : 0;
        totalVisitsHomeHealth += siteData[PREFIX_HOME_HEALTH] ? Number(siteData[PREFIX_HOME_HEALTH].total_visit) : 0;
        totalVisitsParticipant += siteData[PREFIX_PARTICIPANT] ? Number(siteData[PREFIX_PARTICIPANT].total_visit) : 0;

        switch (tabIndex) {
          case 0: {
            if (!("Total Cancelled Telehealth Visits" in tabIndexTotal)) {
              tabIndexTotal["Total Cancelled Telehealth Visits"] = initData;
            }

            tabIndexTotal["Total Cancelled Telehealth Visits"] = {
              siteTeam: {
                cancelled: totalCancelledSiteTeam,
                visits: totalVisitsSiteTeam
              },
              homeHealth: {
                cancelled: totalCancelledHomeHealth,
                visits: totalVisitsHomeHealth
              },
              participant: {
                cancelled: totalCancelledParticipant,
                visits: totalVisitsParticipant
              }
            };
            break;
          }
          case 1: {
            if (!(countryName in tabIndexCountries)) {
              tabIndexCountries[countryName] = initData;
            }
            tabIndexCountries[countryName] = {
              siteTeam: {
                cancelled: totalCountrySiteTeam[countryName].cancelled ? Number(totalCountrySiteTeam[countryName].cancelled) : 0,
                visits: totalCountrySiteTeam[countryName].total ? Number(totalCountrySiteTeam[countryName].total) : 0
              },
              homeHealth: {
                cancelled: totalCountryHomeHealth[countryName].cancelled ? Number(totalCountryHomeHealth[countryName].cancelled) : 0,
                visits: totalCountryHomeHealth[countryName].total ? Number(totalCountryHomeHealth[countryName].total) : 0
              },
              participant: {
                cancelled: totalCountryParticipant[countryName].cancelled ? Number(totalCountryParticipant[countryName].cancelled) : 0,
                visits: totalCountryParticipant[countryName].total ? Number(totalCountryParticipant[countryName].total) : 0
              }
            };
            break;
          }
          case 2: {
            if (!(siteName in tabIndexSite)) {
              tabIndexSite[siteName] = initData;
            }

            tabIndexSite[siteName] = {
              siteTeam: {
                cancelled: siteData[PREFIX_SITE_TEAM] ? Number(siteData[PREFIX_SITE_TEAM].cancelled_count) : 0,
                visits: siteData[PREFIX_SITE_TEAM] ? Number(siteData[PREFIX_SITE_TEAM].total_visit) : 0
              },
              homeHealth: {
                cancelled: siteData[PREFIX_HOME_HEALTH] ? Number(siteData[PREFIX_HOME_HEALTH].cancelled_count) : 0,
                visits: siteData[PREFIX_HOME_HEALTH] ? Number(siteData[PREFIX_HOME_HEALTH].total_visit) : 0
              },
              participant: {
                cancelled: siteData[PREFIX_PARTICIPANT] ? Number(siteData[PREFIX_PARTICIPANT].cancelled_count) : 0,
                visits: siteData[PREFIX_PARTICIPANT] ? Number(siteData[PREFIX_PARTICIPANT].total_visit) : 0
              }
            };
            break;
          }
          default:
            break;
        }
      }
    }
  }

  let tabKey = null;
  // Sort the highest values
  switch (tabIndex) {
    case 0: {
      tabKey = tabIndexTotal;
      break;
    }
    case 1: {
      tabKey = tabIndexCountries;
      break;
    }
    case 2: {
      tabKey = tabIndexSite;
      break;
    }
  }

  for (const item in tabKey) {
    labels.push(item);
    const allCancelled = Number(tabKey[item].siteTeam.cancelled) + Number(tabKey[item].homeHealth.cancelled) + Number(tabKey[item].participant.cancelled);

    const allVisits = Math.max(Number(tabKey[item].siteTeam.visits), Number(tabKey[item].homeHealth.visits), Number(tabKey[item].participant.visits));

    const allCancelledPercentage = Math.round(Number((allCancelled / allVisits) * 100));

    const cancelledSiteTeam = Math.round(Number((tabKey[item].siteTeam.cancelled / allVisits) * 100));
    const cancelledHomeHealth = Math.round(Number((tabKey[item].homeHealth.cancelled / allVisits) * 100));
    const cancelledParticipant = Math.round(Number((tabKey[item].participant.cancelled / allVisits) * 100));

    const siteTeam = {
      cancelled: tabKey[item].siteTeam.cancelled,
      visits: tabKey[item].siteTeam.visits,
      cancelledSiteTeam,
      allCancelled,
      allVisits,
      allCancelledPercentage
    };

    const homeHealth = {
      cancelled: tabKey[item].homeHealth.cancelled,
      visits: tabKey[item].homeHealth.visits,
      cancelledHomeHealth,
      allCancelled,
      allVisits,
      allCancelledPercentage
    };

    const participant = {
      cancelled: tabKey[item].participant.cancelled,
      visits: tabKey[item].participant.visits,
      cancelledParticipant,
      allCancelled,
      allVisits,
      allCancelledPercentage
    };

    dataSiteTeam.push(siteTeam);
    dataHomeHealth.push(homeHealth);
    dataParticipant.push(participant);
  }

  const lengthData = dataSiteTeam.length;

  for (let i = 0; i < lengthData; i++) {
    tmpSortedData.push({
      label: labels[i],
      siteTeam: dataSiteTeam[i],
      homeHealth: dataHomeHealth[i],
      participant: dataParticipant[i]
    });
  }

  const sortedData = _.orderBy(tmpSortedData, ["siteTeam.allCancelled", "label"], ["desc", "asc"]);

  const categoriesData = sortedData.map(item => ({ label: item.label }));
  const siteTeamData = sortedData.map(item => ({
    value: item.siteTeam.cancelledSiteTeam,
    data: item.siteTeam
  }));
  const homeHealthData = sortedData.map(item => ({
    value: item.homeHealth.cancelledHomeHealth,
    data: item.homeHealth
  }));
  const participantData = sortedData.map(item => ({
    value: item.participant.cancelledParticipant,
    data: item.participant
  }));

  series["categories"][0].category = categoriesData;
  series["dataset"][0].data =
    Array.isArray(siteTeamData) && siteTeamData.length > 0
      ? siteTeamData.map(n => {
          return n.value != 0
            ? {
                value: Number(n.value).toFixed(0),
                toolText: generateTooltip(n, "Site Team")
              }
            : { showValue: 0 };
        })
      : [];
  series["dataset"][1].data =
    Array.isArray(homeHealthData) && homeHealthData.length > 0
      ? homeHealthData.map(n => {
          return n.value != 0
            ? {
                value: Number(n.value).toFixed(0),
                toolText: generateTooltip(n, "Home Health")
              }
            : { showValue: 0 };
        })
      : [];
  series["dataset"][2].data =
    Array.isArray(participantData) && participantData.length > 0
      ? participantData.map(n => {
          return n.value != 0
            ? {
                value: Number(n.value).toFixed(0),
                toolText: generateTooltip(n, "Participant")
              }
            : { showValue: 0 };
        })
      : [];

  csvData["categories"] = categoriesData;
  csvData["dataset"][0] = siteTeamData;
  csvData["dataset"][1] = homeHealthData;
  csvData["dataset"][2] = participantData;

  return {
    series,
    csvData
  };
}

function getComplianceRateOverTimeData(rawData, choosenCountries = [], choosenSites = []) {
  const sortedDataByDate = [];
  const response = {};
  const complianceRates = [];
  response.categories = [];
  response.data = [];
  response.lines = [];
  response.data[0] = [];
  response.csvData = [];
  response.meanComplianceRate = 0;
  response.complianceRateStandardDeviation = 0;

  if (!rawData || choosenCountries.length === 0 || choosenSites.length === 0) {
    return response;
  }

  const newRawData = rawData;

  Object.entries(newRawData).forEach(([country, sites]) => {
    Object.entries(sites).forEach(([site, dataArray]) => {
      Object.values(dataArray).forEach(data => {
        if ((choosenSites.includes("all") || choosenSites.includes(site)) && (choosenCountries.includes("all") || choosenCountries.includes(country))) {
          const dateIndex = sortedDataByDate.findIndex(obj => obj.date == data.date);
          if (dateIndex == -1) {
            sortedDataByDate.push({
              date: data.date,
              complete_count: Number(data.complete_count),
              total_count: Number(data.total_count)
            });
          } else {
            sortedDataByDate[dateIndex].complete_count = Number(Number(sortedDataByDate[dateIndex].complete_count) + Number(data.complete_count));
            sortedDataByDate[dateIndex].total_count = Number(Number(sortedDataByDate[dateIndex].total_count) + Number(data.total_count));
          }
        }
      });
    });
  });

  sortedDataByDate.sort(function compare(a, b) {
    const aDate = moment(a.date, "DD MMM YYYY");
    const bDate = moment(b.date, "DD MMM YYYY");

    if (aDate.isValid && bDate.isValid) {
      if (aDate < bDate) {
        return -1;
      } else if (aDate > bDate) {
        return 1;
      } else {
        return 0;
      }
    }
  });

  sortedDataByDate.forEach((data, index) => {
    sortedDataByDate[index].compliance_rate = (Number(data.complete_count) / Number(data.total_count)) * 100;
  });

  sortedDataByDate.forEach(data => {
    complianceRates.push(parseFloat(data.compliance_rate));
    response.categories.push({ label: data.date });
    response.data[0].push({ value: Math.round(data.compliance_rate) });
    response.csvData.push({
      date: data.date,
      compliance_rate: parseFloat(data.compliance_rate).toFixed(2)
    });
  });

  const complianceRateMean = mean(complianceRates);
  response.meanComplianceRate = parseFloat(complianceRateMean).toFixed(2);
  if (complianceRates.length >= 4) {
    const complianceRateStandardDeviation = Number(getStandardDeviation(complianceRates, complianceRateMean));
    const stdDev1Positive = Math.round(complianceRateMean + complianceRateStandardDeviation);
    const stdDev1Negative = Math.round(complianceRateMean - complianceRateStandardDeviation);
    const doubleComplianceRateStandardDeviation = complianceRateStandardDeviation * 2;
    const stdDev2Positive = Math.round(complianceRateMean + doubleComplianceRateStandardDeviation);
    const stdDev2Negative = Math.round(complianceRateMean - doubleComplianceRateStandardDeviation);

    response.complianceRateStandardDeviation = complianceRateStandardDeviation;
    response.lines.push({
      parentyaxis: "p",
      dashed: "1",
      dashLen: "6",
      dashGap: "6",
      thickness: "2",
      color: "#83C6A8",
      trendvaluefont: "robotoregular",
      trendValueFontSize: "12",
      trendValueAlpha: "80",
      value: stdDev1Positive,
      tooltext: "STDEV1: $startvalue%",
      valueonright: "1",
      trendValueBorderPadding: "100",
      displayvalue: " "
    });
    response.lines.push({
      parentyaxis: "p",
      dashed: "1",
      dashLen: "6",
      dashGap: "6",
      thickness: "2",
      color: "#83C6A8",
      trendvaluefont: "robotoregular",
      trendValueFontSize: "12",
      trendValueAlpha: "80",
      value: stdDev1Negative,
      tooltext: "STDEV1: $startvalue%",
      valueonright: "1",
      trendValueBorderPadding: "100",
      displayvalue: " "
    });
    response.lines.push({
      parentyaxis: "p",
      dashed: "1",
      dashLen: "6",
      dashGap: "6",
      thickness: "2",
      color: "#FDBF6F",
      trendvaluefont: "robotoregular",
      trendValueFontSize: "12",
      trendValueAlpha: "80",
      value: stdDev2Positive,
      tooltext: "STDEV2: $startvalue%",
      valueonright: "1",
      trendValueBorderPadding: "100",
      displayvalue: " "
    });
    response.lines.push({
      parentyaxis: "p",
      dashed: "1",
      dashLen: "6",
      dashGap: "6",
      thickness: "2",
      color: "#FDBF6F",
      trendvaluefont: "robotoregular",
      trendValueFontSize: "12",
      trendValueAlpha: "80",
      value: stdDev2Negative,
      tooltext: "STDEV2: $startvalue%",
      valueonright: "1",
      trendValueBorderPadding: "100",
      displayvalue: " "
    });
  }
  return response;
}

function generateDailySensorMapData(rawData, choosenCountries = [], choosenSites = [], activeTab = "fitbit") {
  const result = {
    brandNames: [],
    listDate: [],
    Data: [],
    IsSensorConfigured: true
  };

  const isEmptyData = rawData ? _.isEmpty(rawData[activeTab]) : true;
  const isEmptyChoosenCountries = _.isEmpty(choosenCountries);
  const isEmptyChoosenSites = _.isEmpty(choosenSites);

  const pureRawData = rawData;
  let allDates = [];
  // eslint-disable-next-line no-unused-vars
  const { IsSensorConfigured, StudyStartDate, StudyEndDate, ...sensors } = pureRawData;

  result.IsSensorConfigured = IsSensorConfigured !== 0;

  if (isEmptyChoosenCountries || isEmptyChoosenSites || isEmptyData) {
    return result;
  }

  Object.values(sensors).forEach(sensorData => {
    Object.entries(sensorData).forEach(([country, sites]) => {
      Object.entries(sites).forEach(([site, dataArray]) => {
        Object.values(dataArray).forEach(data => {
          if ((choosenSites.includes("all") || choosenSites.includes(site)) && (choosenCountries.includes("all") || choosenCountries.includes(country))) {
            const matrixActivities = [];
            const { activities, brandName, compliance, lastLogged, threadId } = data;
            result.brandNames.push(brandName);

            activities.sort((a, b) => {
              if (moment(a.loggedDate, "DD MMM YYYY") < moment(b.loggedDate, "DD MMM YYYY")) {
                return -1;
              } else if (moment(a.loggedDate, "DD MMM YYYY") > moment(b.loggedDate, "DD MMM YYYY")) {
                return 1;
              } else {
                return 0;
              }
            });

            activities.forEach(activityData => {
              const { loggedDay, loggedDate, compliantStatus, numberOfTimesDataLogged } = activityData;
              if (!allDates.includes(activityData.loggedDate)) {
                allDates.push(loggedDate);
              }
              let status, classLabel, statusTooltip;
              if (compliantStatus == "non_compliant") {
                status = "Non Compliant";
                classLabel = "daily-sensor-box-noncompliant";
                statusTooltip = "daily-sensor-tooltip-noncompliant";
              } else if (compliantStatus == "not_started") {
                status = "Not Started";
                classLabel = "daily-sensor-box-notstarted";
                statusTooltip = "daily-sensor-tooltip-notstarted";
              } else if (compliantStatus == "compliant") {
                status = "Compliant";
                classLabel = "daily-sensor-box-compliant";
                statusTooltip = "daily-sensor-tooltip-compliant";
              } else {
                status = "EmptyData";
                classLabel = "daily-sensor-box-EmptyData";
                statusTooltip = "daily-sensor-tooltip-EmptyData";
              }
              matrixActivities.push({
                status,
                classLabel,
                statusTooltip,
                loggedDay,
                loggedDate: loggedDate.toUpperCase(),
                compliantStatus,
                numberOfTimesDataLogged,
                brand: brandName,
                participants: threadId
              });
            });
            result.Data.push({
              country,
              site,
              matrixActivities,
              participantNames: threadId,
              participants: threadId,
              brandNames: brandName,
              totalPercentageCompleted: compliance,
              lastlogdate: lastLogged.toUpperCase()
            });
          }
        });
      });
    });
  });

  allDates = allDates.map(date => moment(date, "DD MMM YYYY"));
  let startDate = moment.min(allDates).format("YYYY-MM-DD");
  while (startDate <= moment.max(allDates).format("YYYY-MM-DD")) {
    result.listDate.push({
      date: startDate,
      displayDate: moment(startDate, "YYYY-MM-DD")
        .format("MMM DD")
        .toUpperCase()
    });
    startDate = moment(startDate, "YYYY-MM-DD")
      .add(1, "days")
      .format("YYYY-MM-DD");
  }

  result.Data.sort((a, b) => {
    return a.participantNames.localeCompare(b.participantNames, undefined, {
      numeric: true
    });
  });

  return result;
}

export { getYValueFromGroup, processBubbleData, processComplianceDataByParticipant, processDataForTable, getIntRateValueFromRate, getFilterValueFromRate, generateCsvRate, generateCsvContent, filterVizDataByDate, getComplianceScore, getComplianceInsightNonCompliantActivitiesData, getCancelledTelehealthVisits, getComplianceRateOverTimeData, generateDailySensorMapData };
